import { fetchInitialChatState } from '@/store/thunks/FetchThreads';
import { getThreadFromApi, ThreadId } from '@/models/Thread';
import { GetUserThreadsResponseBody } from '@/api/ApiResponse';
import { openWidget, OpenWidgetPayload } from '@/store/reducers/UIState';
import { ReaderWidgetData, WidgetType } from '@/models/PanelWidgets';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ReaderWidgetStoreState {
  stateForThreads: Record<ThreadId, ReaderWidgetData>;
}

const initialState: ReaderWidgetStoreState = {
  stateForThreads: {},
};

/*************
 * reducers *
 *************/
export const cacheDataForThreadReducer = (
  state: ReaderWidgetStoreState,
  action: PayloadAction<OpenWidgetPayload>,
) => {
  const { threadId, widgetData } = action.payload;
  if (!widgetData || widgetData.type !== WidgetType.READER) {
    return;
  }

  state.stateForThreads[threadId] = widgetData;
};

export const initReaderWidgetStoreReducer = (
  state: ReaderWidgetStoreState,
  action: PayloadAction<GetUserThreadsResponseBody>,
) => {
  action.payload.threads.forEach((thread) => {
    const typedThread = getThreadFromApi(thread);
    // there should only be one reader widget per thread as of now but update this
    // when we allow multiple instances of a widgets per thread
    const readerData = typedThread.uiState?.widgetsInView?.find(
      (widgetData) => widgetData.type === WidgetType.READER,
    );
    if (readerData) {
      state.stateForThreads[typedThread.id] = readerData as ReaderWidgetData;
    }
  });
};

/*************
 * selectors *
 *************/
export const getReaderStateForThreadSelector = (
  state: ReaderWidgetStoreState,
  threadId: ThreadId | undefined,
): ReaderWidgetData | null => {
  if (!threadId) {
    return null;
  }
  return state.stateForThreads[threadId];
};

/*************
 * slice *
 *************/
export const readerWidgetStoreSlice = createSlice({
  name: 'readerWidgetStore',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(openWidget, cacheDataForThreadReducer)
      .addCase(fetchInitialChatState.fulfilled, initReaderWidgetStoreReducer);
  },
  selectors: {
    getReaderStateForThread: getReaderStateForThreadSelector,
  },
});

export const { getReaderStateForThread } = readerWidgetStoreSlice.selectors;
export const readerWidgetStoreReducer = readerWidgetStoreSlice.reducer;
